import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useRealtimeStore, ChatMessage, ControlResult, ControlError } from '@/stores/realtimeStore';
import { getConfig } from '@/api/apiConfig';

// ── Capture SSE 이벤트 데이터 ──
export interface CaptureEventData {
  sessionId: number;
  groupId?: number;
  nodeId?: number;
  nodeName?: string;
  houseId?: number;
  houseName?: string;
  imageUrl?: string;
  capturedAt?: string;
  petName?: string;
  identifiedBy?: string;
}

// ── 옵션 ──
export interface UseRealtimeConnectionOptions {
  enabled?: boolean;
  onChatMessage?: (msg: ChatMessage) => void;
  onControlResult?: (result: ControlResult) => void;
  onControlError?: (error: ControlError) => void;
  onCapturePending?: (data: CaptureEventData) => void;
  onCaptureIdentified?: (data: CaptureEventData) => void;
  onCaptureExpired?: (data: CaptureEventData) => void;
  onStatusChange?: (status: string) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

// 30분 타임아웃 (서버 스펙)
const SSE_STREAM_TIMEOUT = 30 * 60 * 1000;

export function useRealtimeConnection({
  enabled = false,
  onChatMessage,
  onControlResult,
  onControlError,
  onCapturePending,
  onCaptureIdentified,
  onCaptureExpired,
  onStatusChange,
  reconnectInterval = 3000,
  maxReconnectAttempts = 5,
}: UseRealtimeConnectionOptions) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 최신 콜백을 ref로 유지 (의존성 변경에 따른 재연결 방지)
  const onChatMessageRef = useRef(onChatMessage);
  onChatMessageRef.current = onChatMessage;
  const onControlResultRef = useRef(onControlResult);
  onControlResultRef.current = onControlResult;
  const onControlErrorRef = useRef(onControlError);
  onControlErrorRef.current = onControlError;
  const onCapturePendingRef = useRef(onCapturePending);
  onCapturePendingRef.current = onCapturePending;
  const onCaptureIdentifiedRef = useRef(onCaptureIdentified);
  onCaptureIdentifiedRef.current = onCaptureIdentified;
  const onCaptureExpiredRef = useRef(onCaptureExpired);
  onCaptureExpiredRef.current = onCaptureExpired;
  const onStatusRef = useRef(onStatusChange);
  onStatusRef.current = onStatusChange;

  const store = useMemo(() => {
    const s = useRealtimeStore.getState();
    return {
      setSseStatus: s.setSseStatus,
      setSseReconnectAttempts: s.setSseReconnectAttempts,
      setSseShowError: s.setSseShowError,
      setSseEventSource: s.setSseEventSource,
      addChatMessage: s.addChatMessage,
      updateCommandResult: s.updateCommandResult,
      reset: s.reset,
    };
  }, []);

  const baseUrl = getConfig().baseUrl;

  // ════════════════════════════════════════════════════════════════════════════
  // 통합 SSE 연결 — GET /api/v1/events?token=xxx
  // 이벤트 타입: system.connected, control.result, control.error,
  //             chat.message, chat.read, chat.notification, chat.system, chat.room_updated
  // ════════════════════════════════════════════════════════════════════════════
  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${baseUrl}/api/v1/events?token=${encodeURIComponent(token)}`;
    try {
      store.setSseStatus('connecting');
      const es = new EventSource(url);
      esRef.current = es;
      store.setSseEventSource(es);

      // ── system.connected ──
      es.addEventListener('system.connected', () => {
        store.setSseStatus('connected');
        store.setSseReconnectAttempts(0);
        store.setSseShowError(false);
        onStatusRef.current?.('connected');

        // 30분 타임아웃 재연결
        if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
        timeoutTimer.current = setTimeout(() => {
          disconnect();
          connect();
        }, SSE_STREAM_TIMEOUT);
      });

      // ── control.result ──
      es.addEventListener('control.result', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ControlResult;
          store.updateCommandResult(data.commandId, data);
          onControlResultRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── control.error ──
      es.addEventListener('control.error', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ControlError;
          if (data.commandId) {
            store.updateCommandResult(data.commandId, {
              commandId: data.commandId,
              nodeId: data.nodeId ?? 0,
              controlType: '',
              success: false,
              errorCode: data.errorCode,
              errorMessage: data.errorMessage,
            });
          }
          onControlErrorRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── chat.message ──
      es.addEventListener('chat.message', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as ChatMessage;
          store.addChatMessage(data);
          onChatMessageRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── capture.pending — 새 미식별 개체 감지 ──
      es.addEventListener('capture.pending', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as CaptureEventData;
          onCapturePendingRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── capture.identified — 다른 매니저가 식별 완료 ──
      es.addEventListener('capture.identified', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as CaptureEventData;
          onCaptureIdentifiedRef.current?.(data);
        } catch {
          // ignore parse errors
        }
      });

      // ── onerror ──
      es.onerror = () => {
        const wasClosed = es.readyState === EventSource.CLOSED;
        store.setSseStatus('disconnected');
        onStatusRef.current?.('disconnected');
        es.close();
        esRef.current = null;
        store.setSseEventSource(null);

        if (timeoutTimer.current) {
          clearTimeout(timeoutTimer.current);
          timeoutTimer.current = null;
        }

        // readyState CLOSED = 서버가 연결을 거부 (401, 403 등) → 재연결 중단
        if (wasClosed) return;

        const attempts = useRealtimeStore.getState().sseReconnectAttempts;
        if (attempts < maxReconnectAttempts) {
          store.setSseReconnectAttempts(attempts + 1);
          reconnectTimer.current = setTimeout(connect, reconnectInterval);
        } else {
          store.setSseShowError(true);
        }
      };
    } catch {
      store.setSseStatus('disconnected');
      store.setSseShowError(true);
    }
  }, [baseUrl, reconnectInterval, maxReconnectAttempts, store]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (timeoutTimer.current) {
      clearTimeout(timeoutTimer.current);
      timeoutTimer.current = null;
    }
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
      store.setSseEventSource(null);
    }
    store.setSseStatus('disconnected');
  }, [store]);

  const reconnect = useCallback(() => {
    disconnect();
    store.setSseReconnectAttempts(0);
    store.setSseShowError(false);
    connect();
  }, [connect, disconnect, store]);

  // ── 라이프사이클 ──
  useEffect(() => {
    if (enabled) connect();
    else disconnect();
    return () => disconnect();
  }, [enabled, connect, disconnect]);

  // ── 읽기 전용 상태 구독 ──
  const sseStatus = useRealtimeStore((s) => s.sseStatus);
  const reconnectAttempts = useRealtimeStore((s) => s.sseReconnectAttempts);
  const showError = useRealtimeStore((s) => s.sseShowError);
  const chatMessages = useRealtimeStore((s) => s.chatMessages);

  return {
    // 연결 상태 (채팅 useConnection 호환)
    isConnected: sseStatus === 'connected',
    connectionStatus: sseStatus,
    reconnectAttempts,
    showConnectionError: showError,
    messages: chatMessages,
    messageContext: null,
    allMessageContexts: [],
    connect,
    disconnect,
    reconnect,
  };
}
